/**
 * Cart duck — the only thing that needs Redux/SDK is "Köp nu" from the
 * varukorg flyout. The cart itself lives in localStorage (see
 * util/cart.js); these thunks just replay a cart line (or a whole
 * seller-group) into the existing single-vendor checkout.
 *
 * They mirror ListingPage.shared.handleSubmit, but re-fetch the listing
 * fresh so the CheckoutPage always gets a valid entity (Money price,
 * UUID id, author) rather than trying to rehydrate one from stored JSON.
 *
 * No reducer is registered — bare thunks are enough (redux-thunk runs
 * them regardless of whether a slice exists in the root reducer).
 */
import { types as sdkTypes, createImageVariantConfig } from '../util/sdkLoader';
import { denormalisedResponseEntities } from '../util/data';
import { postSellerPaymentStatus } from '../util/api';
import { createResourceLocatorString, findRouteByRouteName } from '../util/routes';
import { createSlug } from '../util/urlHelpers';
import { storableError } from '../util/errors';
import { initializeCardPaymentData } from './stripe.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const BUY_NOW_REQUEST = 'app/cart/BUY_NOW_REQUEST';
export const BUY_NOW_SUCCESS = 'app/cart/BUY_NOW_SUCCESS';
export const BUY_NOW_ERROR = 'app/cart/BUY_NOW_ERROR';

export const buyNowRequest = listingId => ({ type: BUY_NOW_REQUEST, payload: { listingId } });
export const buyNowSuccess = () => ({ type: BUY_NOW_SUCCESS });
export const buyNowError = e => ({ type: BUY_NOW_ERROR, error: true, payload: e });

// ================ Helpers ================ //

const imageVariantParams = config => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    include: ['author', 'currentStock', 'images'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const toQuantity = raw => (raw ? Number.parseInt(raw, 10) || 1 : 1);

/* P25009-452 BR-2 — the buyer is never taken into a checkout that cannot be
 * paid. Before handing the cart off, ask the server whether the vendor can
 * take payment (the same predicate the initiate endpoints enforce). Only an
 * explicit "no" blocks — an endpoint hiccup fails open, because the
 * checkout's own guard still catches the rare unpayable seller. The thrown
 * error carries `code` so the cart surfaces can say why instead of failing
 * silently. */
export const SELLER_NOT_PAYABLE = 'seller-not-payable';
const assertSellerCanTakePayment = listing => {
  const authorId = listing?.author?.id?.uuid;
  if (!authorId) {
    return Promise.resolve();
  }
  return postSellerPaymentStatus({ authorId }).then(
    res => {
      if (res?.canTakePayment === false) {
        const err = new Error('Säljaren kan inte ta emot betalningar ännu.');
        err.code = SELLER_NOT_PAYABLE;
        throw err;
      }
    },
    () => {} // status endpoint failed → fail open
  );
};

// Seed CheckoutPage state, clear any stale Stripe error, then client-side
// push to the checkout. Shared by both thunks.
const handoffToCheckout = (dispatch, listing, orderData, history, routes) => {
  const initialValues = { listing, orderData, confirmPaymentError: null };
  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
  dispatch(setInitialValues(initialValues));
  dispatch(initializeCardPaymentData());
  dispatch(buyNowSuccess());
  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};

// ================ Thunks ================ //

/**
 * Re-fetch the cart's listings fresh from the Marketplace API so the cart
 * page can validate availability, stock and live price (never trust the
 * prices stored in localStorage — spec C5).
 *
 * Resolves to an array aligned with `listingIds`: each entry is the
 * denormalised listing entity, or `null` when the listing is gone /
 * unpublished / not fetchable (treated as "no longer available").
 *
 * @param {Array}  listingIds  UUID strings
 * @param {Object} config      app configuration (image variants)
 */
export const fetchCartListings = (listingIds, config) => (dispatch, getState, sdk) => {
  const ids = (Array.isArray(listingIds) ? listingIds : []).filter(Boolean);
  if (ids.length === 0) {
    return Promise.resolve([]);
  }
  const params = imageVariantParams(config);
  return Promise.all(
    ids.map(id =>
      sdk.listings
        .show({ id: new UUID(id), ...params })
        .then(resp => denormalisedResponseEntities(resp)[0] || null)
        .catch(() => null)
    )
  );
};

/**
 * Take a single cart line to the checkout.
 *
 * @param {String} listingId  UUID string of the listing to buy
 * @param {Object} orderData  { quantity, deliveryMethod?, selectedServiceIds? }
 * @param {Object} history    react-router history (client-side push)
 * @param {Array}  routes     route configuration
 * @param {Object} config     app configuration (for image variants)
 */
export const buyNow = (listingId, orderData, history, routes, config) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(buyNowRequest(listingId));

  return sdk.listings
    .show({ id: new UUID(listingId), ...imageVariantParams(config) })
    .then(response => {
      const [listing] = denormalisedResponseEntities(response);
      if (!listing?.id || !listing?.attributes?.price) {
        throw new Error('Listing is no longer available for purchase.');
      }

      const quantity = toQuantity(orderData?.quantity);
      return assertSellerCanTakePayment(listing).then(() => {
        handoffToCheckout(
          dispatch,
          listing,
          { ...(orderData || {}), quantity, stockReservationQuantity: quantity },
          history,
          routes
        );
        return listing;
      });
    })
    .catch(e => {
      dispatch(buyNowError(storableError(e)));
      throw e;
    });
};

/**
 * Take ALL of one vendor's cart lines to the checkout as a single order.
 *
 * The first item becomes the transaction's primary listing; the rest ride
 * along as `orderData.cartItems` ([{ listingId, quantity }]) which the
 * privileged endpoints turn into bundled line items (one PaymentIntent, one
 * payout) and the post-payment step uses to decrement their stock.
 *
 * All items must belong to the same vendor (the server re-checks and rejects
 * cross-vendor bundles); the caller groups the cart by authorId first.
 *
 * @param {Array}  items    same-vendor cart items ({ listingId, orderData? })
 * @param {Object} history  react-router history
 * @param {Array}  routes   route configuration
 * @param {Object} config   app configuration (image variants)
 */
export const buyVendorCart = (items, history, routes, config) => (dispatch, getState, sdk) => {
  const list = Array.isArray(items) ? items.filter(i => i && i.listingId) : [];
  if (list.length === 0) {
    return Promise.resolve();
  }
  const [primary, ...rest] = list;
  dispatch(buyNowRequest(primary.listingId));

  return sdk.listings
    .show({ id: new UUID(primary.listingId), ...imageVariantParams(config) })
    .then(response => {
      const [listing] = denormalisedResponseEntities(response);
      if (!listing?.id || !listing?.attributes?.price) {
        throw new Error('Listing is no longer available for purchase.');
      }

      const primaryQty = toQuantity(primary.orderData?.quantity);
      const cartItems = rest.map(i => ({
        listingId: i.listingId,
        quantity: toQuantity(i.orderData?.quantity),
        // Title is display-only (shown in the order breakdown); prices + stock
        // are always resolved server-side from the listing, never from here.
        ...(i.title ? { title: i.title } : {}),
      }));

      return assertSellerCanTakePayment(listing).then(() => {
        handoffToCheckout(
          dispatch,
          listing,
          {
            ...(primary.orderData || {}),
            quantity: primaryQty,
            stockReservationQuantity: primaryQty,
            cartItems,
          },
          history,
          routes
        );
        return listing;
      });
    })
    .catch(e => {
      dispatch(buyNowError(storableError(e)));
      throw e;
    });
};
